import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { images } from '../../data/content';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const hardcodedCaptions = {
  en: [
    { label: 'Crystal Healing', sub: 'Energy · Balance' },
    { label: 'Ensan Farid', sub: 'Identity Journey' },
    { label: 'Healthy Tuesday', sub: 'Healing Community' },
    { label: 'Mona Bizzari', sub: 'Your Guide' },
    { label: 'Positivity', sub: 'Inner Work' },
    { label: 'Youm El Mona', sub: 'Full-Day Immersion' },
  ],
  ar: [
    { label: 'شفاء الكريستال', sub: 'الطاقة · التوازن' },
    { label: 'إنسان فريد', sub: 'رحلة الهوية' },
    { label: 'الثلاثاء الصحي', sub: 'دائرة الشفاء' },
    { label: 'منى بزاري', sub: 'مرشدتك' },
    { label: 'الإيجابية', sub: 'العمل الداخلي' },
    { label: 'يوم المنى', sub: 'يوم غامر كامل' },
  ],
};

const Lightbox = ({ items, currentIndex, onClose, onPrev, onNext, isRTL }) => {
  const dialogRef = useRef(null);
  const triggerRef = useRef(null);
  const item = items[currentIndex];

  useEffect(() => {
    triggerRef.current = document.activeElement;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') { e.preventDefault(); isRTL ? onNext() : onPrev(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); isRTL ? onPrev() : onNext(); }
      if (e.key === 'Tab') {
        const focusable = dialogRef.current?.querySelectorAll('button, [tabindex="0"]');
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    if (dialogRef.current) dialogRef.current.focus();
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
      if (triggerRef.current && typeof triggerRef.current.focus === 'function') {
        triggerRef.current.focus();
      }
    };
  }, [onClose, onPrev, onNext, isRTL]);

  if (!item) return null;

  const isVideo = item.isVideo;
  const caption = item.caption || '';
  const sub = item.sub || '';

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={caption || 'Gallery lightbox'}
      tabIndex={-1}
      className="fixed inset-0 z-[9999] flex items-center justify-center outline-none"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-charcoal/95 backdrop-blur-sm" />

      <button
        aria-label="Close lightbox"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-cream transition-colors"
      >
        <X size={22} />
      </button>

      {items.length > 1 && (
        <>
          <button
            aria-label={isRTL ? 'Next image' : 'Previous image'}
            onClick={(e) => { e.stopPropagation(); isRTL ? onNext() : onPrev(); }}
            className="absolute left-2 sm:left-6 z-10 p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/20 text-cream transition-colors"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            aria-label={isRTL ? 'Previous image' : 'Next image'}
            onClick={(e) => { e.stopPropagation(); isRTL ? onPrev() : onNext(); }}
            className="absolute right-2 sm:right-6 z-10 p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/20 text-cream transition-colors"
          >
            <ChevronRight size={24} />
          </button>
        </>
      )}

      <div
        className="relative z-10 flex flex-col items-center max-w-[90vw] max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {isVideo ? (
          <video
            src={item.url}
            autoPlay
            muted
            loop
            playsInline
            controls
            className="max-w-full max-h-[75vh] rounded-xl object-contain"
          />
        ) : (
          <img
            src={item.url}
            alt={caption || 'Gallery image'}
            className="max-w-full max-h-[75vh] rounded-xl object-contain"
          />
        )}

        {(caption || sub) && (
          <div className="mt-4 text-center px-4">
            {caption && <p className="text-cream font-serif text-lg sm:text-xl">{caption}</p>}
            {sub && <p className="text-gold text-xs uppercase tracking-widest mt-1">{sub}</p>}
          </div>
        )}

        <p className="text-cream-500/50 text-xs mt-3" aria-live="polite">
          {currentIndex + 1} / {items.length}
        </p>
      </div>
    </div>
  );
};

const GallerySection = () => {
  const { t, isRTL } = useLanguage();
  const [hovered, setHovered] = useState(null);
  const [cmsItems, setCmsItems] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetch(`${backendUrl}/api/cms/gallery`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setCmsItems(Array.isArray(data) ? data : []))
      .catch(() => setCmsItems([]));
  }, [backendUrl]);

  const useCms = cmsItems && cmsItems.length > 0;
  const caps = isRTL ? hardcodedCaptions.ar : hardcodedCaptions.en;
  const imgs = images.gallery;

  const lightboxItems = useCms
    ? cmsItems.map(item => ({
        url: item.url,
        caption: isRTL ? (item.caption_ar || item.caption || '') : (item.caption || ''),
        sub: '',
        isVideo: item.file_type?.startsWith('video') || /\.(mp4|webm|mov)$/i.test(item.url || ''),
      }))
    : imgs.map((src, i) => ({
        url: src,
        caption: caps[i]?.label || '',
        sub: caps[i]?.sub || '',
        isVideo: false,
      }));

  useEffect(() => {
    if (lightboxIndex !== null && lightboxIndex >= lightboxItems.length) {
      setLightboxIndex(null);
    }
  }, [lightboxItems.length, lightboxIndex]);

  const openLightbox = useCallback((index) => setLightboxIndex(index), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const goPrev = useCallback(() => {
    setLightboxIndex(prev => {
      if (prev === null || lightboxItems.length === 0) return null;
      return prev <= 0 ? lightboxItems.length - 1 : prev - 1;
    });
  }, [lightboxItems.length]);
  const goNext = useCallback(() => {
    setLightboxIndex(prev => {
      if (prev === null || lightboxItems.length === 0) return null;
      return prev >= lightboxItems.length - 1 ? 0 : prev + 1;
    });
  }, [lightboxItems.length]);

  const GalleryTile = ({ children, index, className = '' }) => (
    <div
      role="button"
      tabIndex={0}
      className={`relative overflow-hidden rounded-xl group cursor-pointer ${className}`}
      onMouseEnter={() => setHovered(index)}
      onMouseLeave={() => setHovered(null)}
      onClick={() => openLightbox(index)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openLightbox(index); } }}
    >
      {children}
    </div>
  );

  const HardcodedImage = ({ src, index, className = '' }) => {
    const cap = caps[index];
    return (
      <GalleryTile index={index} className={className}>
        <img
          src={src}
          alt={cap?.label || `Moment ${index + 1}`}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-charcoal/10 to-transparent pointer-events-none" />
        <div className={`absolute inset-0 bg-charcoal/40 transition-opacity duration-300 ${hovered === index ? 'opacity-100' : 'opacity-0'} pointer-events-none`} />
        {cap && (
          <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
            <p className="text-cream font-serif text-sm sm:text-base lg:text-lg leading-tight drop-shadow-lg">{cap.label}</p>
            <p className="text-gold text-[10px] sm:text-xs uppercase tracking-widest mt-0.5 drop-shadow">{cap.sub}</p>
          </div>
        )}
      </GalleryTile>
    );
  };

  const CmsItem = ({ item, index }) => {
    const isVideo = item.file_type?.startsWith('video') || /\.(mp4|webm|mov)$/i.test(item.url || '');
    const caption = isRTL ? (item.caption_ar || item.caption || '') : (item.caption || '');
    const isHero = index === 0;

    return (
      <GalleryTile index={index} className={isHero ? 'col-span-2 md:col-span-2 row-span-2' : 'col-span-1'}>
        {isVideo ? (
          <video
            src={item.url}
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <img
            src={item.url}
            alt={caption || item.alt_text || `Moment ${index + 1}`}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-charcoal/10 to-transparent pointer-events-none" />
        <div className={`absolute inset-0 bg-charcoal/40 transition-opacity duration-300 ${hovered === index ? 'opacity-100' : 'opacity-0'} pointer-events-none`} />
        {caption && (
          <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
            <p className="text-cream font-serif text-sm sm:text-base lg:text-lg leading-tight drop-shadow-lg">{caption}</p>
          </div>
        )}
      </GalleryTile>
    );
  };

  return (
    <section id="gallery" className="section-padding bg-charcoal relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold/25 to-transparent" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-10">

        <div className={`flex flex-col ${isRTL ? 'items-end text-right' : 'items-start text-left'} mb-10 sm:mb-14`}>
          <p className="text-gold text-xs uppercase tracking-[0.3em] mb-3 font-medium">
            {t.gallery.tagline}
          </p>
          <div className="flex items-baseline gap-4 sm:gap-6 flex-wrap">
            <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-cream leading-none tracking-tight">
              {t.gallery.headline}
            </h2>
            <div className="hidden sm:block flex-1 h-px bg-gold/25 self-center min-w-[60px]" />
          </div>
          <p className="text-cream-400 text-sm sm:text-base mt-4 max-w-md leading-relaxed">
            {t.gallery.description}
          </p>
        </div>

        {useCms ? (
          <div className="grid grid-cols-2 md:grid-cols-3 auto-rows-[200px] sm:auto-rows-[220px] md:auto-rows-[190px] lg:auto-rows-[210px] gap-3 sm:gap-4">
            {cmsItems.map((item, i) => (
              <CmsItem key={item.id} item={item} index={i} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-12 auto-rows-[180px] sm:auto-rows-[200px] md:auto-rows-[160px] lg:auto-rows-[180px] gap-3 sm:gap-4">
            <HardcodedImage src={imgs[0]} index={0} className="col-span-2 md:col-span-5 md:row-span-3" />
            <HardcodedImage src={imgs[1]} index={1} className="col-span-1 md:col-span-4 md:row-span-2" />
            <HardcodedImage src={imgs[2]} index={2} className="col-span-1 md:col-span-3 md:row-span-1" />
            <HardcodedImage src={imgs[3]} index={3} className="col-span-1 md:col-span-3 md:row-span-1" />
            <HardcodedImage src={imgs[4]} index={4} className="col-span-1 md:col-span-4 md:row-span-1" />
            <HardcodedImage src={imgs[5]} index={5} className="col-span-2 md:col-span-7 md:row-span-1" />
          </div>
        )}

        <div className="mt-8 sm:mt-10 flex items-center gap-4">
          <div className="flex-1 h-px bg-gold/12" />
          <p className="text-cream-500 text-xs uppercase tracking-[0.2em] flex-shrink-0">
            {isRTL ? 'لحظات حقيقية · سيدات حقيقيات · تحوّل حقيقي' : 'Real Moments · Real Women · Real Transformation'}
          </p>
          <div className="flex-1 h-px bg-gold/12" />
        </div>

      </div>

      {lightboxIndex !== null && lightboxIndex < lightboxItems.length && (
        <Lightbox
          items={lightboxItems}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onPrev={goPrev}
          onNext={goNext}
          isRTL={isRTL}
        />
      )}
    </section>
  );
};

export default GallerySection;
